import React, { useState, useEffect } from "react";
import { DmsTextField, DmsCheckBox, DmsSelect, DmsTextFieldPhone, DmsTextFieldEmail, intl, fetchData } from "dmsbasereact";
import { DmsAutocomplete } from "../DmsAutoComplete/DmsAutoComplete";
import { Grid, Box, Typography, FormGroup, FormControlLabel, Checkbox, Radio, RadioGroup, Link } from "@mui/material";
import { LegalDocumentDialog } from "../LegalDocumentDialog/LegalDocumentDialog";
import { LocalizationProvider } from '@mui/x-date-pickers';
import DmsDatePicker from "../DmsDatePicker/DmsDatePicker";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import { getGenderEnum } from "../../constants/GenderEnum";
import DmsSelectRGIssuingAgency from "../DmsSelectRGIssuingAgency/DmsSelectRGIssuingAgency";
import { DmsTextFieldCpf } from "../DmsTextFieldCpf/DmsTextFieldCpf";
import { DmsTextFieldCep } from "../DmsTextFieldCep";

export const TabPatientCreate = ({
    cpfValue,
    setCpfValue,
    cpfIsValid,
    setCpfIsValid,
    startDate,
    setStartDate,
    selectedGenderOption,
    setSelectedGenderOption,
    hasPregnancyPotential,
    setHasPregnancyPotential,
    menstrualCycleRegular,
    setMenstrualCycleRegular,
    hasResponsibleLegal,
    setHasResponsibleLegal,
    stateOptions,
    genderOptions,
    patientName,
    setPatientName,
    // Address fields
    zipCode,
    setZipCode,
    city,
    setCity,
    state,
    setState,
    address,
    setAddress,
    addressNumber,
    setAddressNumber,
    phone,
    setPhone,
    email,
    setEmail,
    rg,
    setRg,
    rgIssuingAgency,
    setRgIssuingAgency,
    // Contraceptive methods for female patients with pregnancy potential
    highlyEffectiveMethods,
    setHighlyEffectiveMethods,
    barrierMethods,
    setBarrierMethods,
    // Infertility reason for female patients without pregnancy potential
    selectedInfertilityReason,
    setSelectedInfertilityReason,
    setSelectedInfertilityReasonName,
    // Patient consent fields
    dataUsageConsentAccepted,
    setDataUsageConsentAccepted,
    contactAuthorization,
    setContactAuthorization,
    // Field errors for validation
    fieldErrors = {},
}) => {
    const [highlyEffectiveMethodOptions, setHighlyEffectiveMethodOptions] = useState([]);
    const [barrierMethodOptions, setBarrierMethodOptions] = useState([]);

    // Legal document modal state
    const [legalDocument, setLegalDocument] = useState(null);
    const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
    const [loadingDocument, setLoadingDocument] = useState(false);

    // AR-009 Item 9.13: Check if patient is under 18 years old
    const isMinor = startDate && dayjs(startDate).isValid()
        ? dayjs().diff(dayjs(startDate), 'year') < 18
        : false;

    // Automatically check "Has Responsible Legal" for minors
    useEffect(() => {
        if (isMinor) {
            setHasResponsibleLegal(true);
        }
    }, [isMinor, setHasResponsibleLegal]);

    // Fetch contraceptive methods from backend
    useEffect(() => {
        const fetchContraceptiveMethods = async () => {
            try {
                const response = await fetchData('/contraceptive_method/all', 'GET', true);
                if (response?.data) {
                    const methods = response.data.data;
                    
                    // Ensure methods is always an array
                    const methodsArray = Array.isArray(methods) ? methods : [];
                    // Filter by category
                    const highlyEffective = methodsArray
                        .filter(method => method.category === 'highly_effective')
                        .map(method => ({ value: method.pk, label: method.reason }));
                    
                    const barrier = methodsArray
                        .filter(method => method.category === 'barrier')
                        .map(method => ({ value: method.pk, label: method.reason }));

                    setHighlyEffectiveMethodOptions(highlyEffective);
                    setBarrierMethodOptions(barrier);


                }
            } catch (error) {
                console.error('Error fetching contraceptive methods:', error);
            }
        };

        fetchContraceptiveMethods();
    }, []);

    // Fetch legal document for patient data consent
    useEffect(() => {
        const fetchLegalDocument = async () => {
            setLoadingDocument(true);
            try {
                const response = await fetchData('/legal-document/public/patient_data_consent', 'GET', false);
                if (response?.data) {
                    setLegalDocument(response.data);
                }
            } catch (error) {
                console.error('Error fetching legal document:', error);
            } finally {
                setLoadingDocument(false);
            }
        };

        fetchLegalDocument();
    }, []);

    const handleOpenDocument = () => {
        setDocumentDialogOpen(true);
    };

    const handleCloseDocument = () => {
        setDocumentDialogOpen(false);
    };

    const handleGenderChange = (selectedOption) => {
        setSelectedGenderOption(selectedOption);

        if (selectedOption === getGenderEnum().M.value) {
            setHasPregnancyPotential(false);
            setMenstrualCycleRegular(false);
            setHighlyEffectiveMethods([]);
            setBarrierMethods([]);
            setSelectedInfertilityReason(undefined);
        }
    };

    const onChangePregnancyCheckbox = (event) => {
        const isChecked = event.target.checked;
        setHasPregnancyPotential(isChecked);

        // Clear opposite value when switching
        if (isChecked) {
            // Switching to fertile - clear infertility reason
            setSelectedInfertilityReason(undefined);
        } else {
            // Switching to infertile - clear contraceptive methods and menstrual cycle
            setHighlyEffectiveMethods([]);
            setBarrierMethods([]);
            setMenstrualCycleRegular(false);
        }
    }

    const onChangeMenstrualCheckbox = (event) => {
        setMenstrualCycleRegular(event.target.checked)
    }

    const onChangeResponsibleLegalCheckbox = (event) => {
        setHasResponsibleLegal(event.target.checked)
    }

    const handleBirthDateChange = (newValue) => {
        setStartDate(newValue);

        if (newValue && newValue.isValid()) {
            const today = dayjs();
            const age = today.diff(newValue, 'year');
            if (age < 18) {
                setHasResponsibleLegal(true);
            }
        }
    };

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <DmsCheckBox
                        label={intl.get("HasResponsibleLegal").d("Has Responsible Legal")}
                        checked={isMinor || hasResponsibleLegal}
                        onChange={onChangeResponsibleLegalCheckbox}
                        disabled={isMinor}
                        helperText={isMinor ? intl.get("ResponsibleLegalRequiredForMinors").d("Responsible legal is required for patients under 18 years old") : ""}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DmsTextField
                        name="patient_name"
                        label={intl.get("Name").d("Name") + " *"}
                        fullWidth
                        value={patientName}
                        onChange={(e) => setPatientName(e.target.value)}
                        error={!!fieldErrors.patientName}
                        helperText={fieldErrors.patientName}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DmsTextFieldCpf
                        name="patient_cpf"
                        fullWidth
                        value={cpfValue}
                        onChange={(e) => setCpfValue(e.target.value)}
                        onValidationChange={(isValid) => setCpfIsValid(isValid)}
                        label={intl.get("CPF").d("CPF") + " *"}
                        error={!!fieldErrors.cpf}
                        helperText={fieldErrors.cpf}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DmsDatePicker
                            label={intl.get("BirthDate").d("Birth Date") + " *"}
                            value={startDate}
                            onChange={handleBirthDateChange}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: !!fieldErrors.birthDate,
                                    helperText: fieldErrors.birthDate
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="patient_rg"
                        label={intl.get("RG").d("RG")}
                        fullWidth
                        value={rg}
                        onChange={(e) => setRg(e.target.value)}
                        inputProps={{ maxLength: 20 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsSelectRGIssuingAgency
                        name="patient_rg_issuing_agency"
                        value={rgIssuingAgency}
                        onChange={(value) => setRgIssuingAgency(value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DmsTextFieldCep
                        label={intl.get("CEP").d("Zip Code")}
                        fullWidth
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        required
                        error={!!fieldErrors.zipCode}
                        helperText={fieldErrors.zipCode}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="patient_city"
                        label={intl.get("City").d("City")}
                        fullWidth
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                        error={!!fieldErrors.city}
                        helperText={fieldErrors.city}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsAutocomplete
                        options={stateOptions}
                        label={intl.get('State').d('State')}
                        value={state}
                        onChange={(value) => setState(value)}
                        fullWidth
                        required
                        error={!!fieldErrors.state}
                        helperText={fieldErrors.state}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="patient_address"
                        label={intl.get("Address").d("Address")}
                        fullWidth
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        required
                        error={!!fieldErrors.address}
                        helperText={fieldErrors.address}
                        inputProps={{ maxLength: 255 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="patient_address_number"
                        label={intl.get("Number").d("Number")}
                        fullWidth
                        value={addressNumber}
                        onChange={(e) => setAddressNumber(e.target.value)}
                        required
                        error={!!fieldErrors.addressNumber}
                        helperText={fieldErrors.addressNumber}
                        inputProps={{ maxLength: 10 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextFieldPhone
                        name="patient_phone"
                        label={intl.get("Phone").d("Phone")}
                        fullWidth
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextFieldEmail
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DmsSelect
                        value={selectedGenderOption}
                        options={genderOptions}
                        onChange={handleGenderChange}
                        label={intl.get('Gender').d('Gender') + " *"}
                        error={!!fieldErrors.gender}
                        helperText={fieldErrors.gender}
                    />
                </Grid>
                {selectedGenderOption === getGenderEnum().F.value && (
                    <Grid item xs={12}>
                        <DmsCheckBox
                            label={intl.get("FertilityPotencial").d("Fertility Potential")}
                            checked={hasPregnancyPotential}
                            onChange={onChangePregnancyCheckbox}
                        />
                    </Grid>
                )}
                {selectedGenderOption === getGenderEnum().F.value && hasPregnancyPotential && (
                    <Grid item xs={12}>
                        <DmsCheckBox
                            label={intl.get("MenstrualCycleRegular").d("Menstrual Cycle Regular")}
                            checked={menstrualCycleRegular}
                            onChange={onChangeMenstrualCheckbox}
                        />
                    </Grid>
                )}
                {selectedGenderOption === getGenderEnum().F.value && hasPregnancyPotential && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                                {intl.get("ContraceptiveMethodsRequired").d("Contraceptive Methods (Required)")} *
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {intl.get("ContraceptiveMethodsInstruction").d("Select at least one highly effective method and one barrier method that the patient is using.")}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                p: 2,
                                bgcolor: fieldErrors.highlyEffectiveMethods ? 'error.lighter' : 'grey.50',
                                borderRadius: 1,
                                height: '100%',
                                border: fieldErrors.highlyEffectiveMethods ? '1px solid' : 'none',
                                borderColor: fieldErrors.highlyEffectiveMethods ? 'error.main' : 'transparent'
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: 'success.dark' }}>
                                    {intl.get("HighlyEffectiveMethods").d("Highly Effective Method")} *
                                </Typography>
                                {fieldErrors.highlyEffectiveMethods && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                                        {fieldErrors.highlyEffectiveMethods}
                                    </Typography>
                                )}
                                <FormGroup>
                                    {highlyEffectiveMethodOptions.map((method) => (
                                        <FormControlLabel
                                            key={method.value}
                                            control={
                                                <Checkbox
                                                    checked={highlyEffectiveMethods.includes(method.value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setHighlyEffectiveMethods([...highlyEffectiveMethods, method.value]);
                                                        } else {
                                                            setHighlyEffectiveMethods(highlyEffectiveMethods.filter(m => m !== method.value));
                                                        }
                                                    }}
                                                    size="small"
                                                    color="success"
                                                />
                                            }
                                            label={<Typography variant="body2">{method.label}</Typography>}
                                            sx={{ mb: 0.5 }}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                p: 2,
                                bgcolor: fieldErrors.barrierMethods ? 'error.lighter' : 'grey.50',
                                borderRadius: 1,
                                height: '100%',
                                border: fieldErrors.barrierMethods ? '1px solid' : 'none',
                                borderColor: fieldErrors.barrierMethods ? 'error.main' : 'transparent'
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: 'warning.dark' }}>
                                    {intl.get("BarrierMethods").d("Barrier Method")} *
                                </Typography>
                                {fieldErrors.barrierMethods && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                                        {fieldErrors.barrierMethods}
                                    </Typography>
                                )}
                                <FormGroup>
                                    {barrierMethodOptions.map((method) => (
                                        <FormControlLabel
                                            key={method.value}
                                            control={
                                                <Checkbox
                                                    checked={barrierMethods.includes(method.value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setBarrierMethods([...barrierMethods, method.value]);
                                                        } else {
                                                            setBarrierMethods(barrierMethods.filter(m => m !== method.value));
                                                        }
                                                    }}
                                                    size="small"
                                                    color="warning"
                                                />
                                            }
                                            label={<Typography variant="body2">{method.label}</Typography>}
                                            sx={{ mb: 0.5 }}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        </Grid>
                    </>
                )}
                {selectedGenderOption === getGenderEnum().F.value && !hasPregnancyPotential && (
                    <Grid item xs={12}>
                        <DmsAutocomplete
                            properties={{ label: 'reason', value: 'pk' }}
                            needs_authentication={true}
                            url={'/infertility_reason/all'}
                            onChange={(selectedOption) => setSelectedInfertilityReason(selectedOption)}
                            onOptionChange={(option) => setSelectedInfertilityReasonName && setSelectedInfertilityReasonName(option?.label || '')}
                            value={selectedInfertilityReason}
                            label={intl.get('InfertilityReason').d('Infertility Reason') + " *"}
                            required
                            error={!!fieldErrors.selectedInfertilityReason}
                            helperText={fieldErrors.selectedInfertilityReason}
                        />
                    </Grid>
                )}

                {/* Patient Data Consent Section */}
                <Grid item xs={12}>
                    <Box sx={{
                        mt: 3,
                        p: 3,
                        bgcolor: 'info.lighter',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'info.light'
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'info.dark' }}>
                            {intl.get("PatientDataConsent").d("Consentimento para Coleta e Uso de Dados")}
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            {intl.get("PatientDataConsentDescription").d("Ao prosseguir com o cadastro, você confirma que obteve o consentimento livre, informado e inequívoco do paciente ou de seu representante legal para a coleta, uso e compartilhamento dos dados pessoais conforme descrito nos termos abaixo.")}
                            {" "}
                            <Link
                                component="button"
                                variant="body2"
                                onClick={handleOpenDocument}
                                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                            >
                                {intl.get("ViewFullConsentTerms").d("Ver termos completos")}
                            </Link>
                        </Typography>

                        {/* Data Usage Consent Checkbox */}
                        <Box sx={{
                            p: 2,
                            bgcolor: fieldErrors.dataUsageConsentAccepted ? 'error.lighter' : 'background.paper',
                            borderRadius: 1,
                            mb: 2,
                            border: fieldErrors.dataUsageConsentAccepted ? '1px solid' : 'none',
                            borderColor: fieldErrors.dataUsageConsentAccepted ? 'error.main' : 'transparent'
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={dataUsageConsentAccepted}
                                        onChange={(e) => setDataUsageConsentAccepted(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {intl.get("DataUsageConsentLabel").d("O paciente, ou seu responsável legal, aceita os termos de coleta e uso dos seus dados para cadastro como paciente no Programa Lena")} *
                                    </Typography>
                                }
                            />
                            {fieldErrors.dataUsageConsentAccepted && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4 }}>
                                    {fieldErrors.dataUsageConsentAccepted}
                                </Typography>
                            )}
                        </Box>

                        {/* Contact Authorization Radio Button */}
                        <Box sx={{
                            p: 2,
                            bgcolor: fieldErrors.contactAuthorization ? 'error.lighter' : 'background.paper',
                            borderRadius: 1,
                            border: fieldErrors.contactAuthorization ? '1px solid' : 'none',
                            borderColor: fieldErrors.contactAuthorization ? 'error.main' : 'transparent'
                        }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                                {intl.get("ContactAuthorizationQuestion").d("O paciente autoriza recebimento de contato pelos canais informados, apenas para finalidades não comerciais?")} *
                            </Typography>
                            <RadioGroup
                                value={contactAuthorization === null ? '' : contactAuthorization ? 'yes' : 'no'}
                                onChange={(e) => setContactAuthorization(e.target.value === 'yes')}
                            >
                                <FormControlLabel
                                    value="yes"
                                    control={<Radio color="success" />}
                                    label={intl.get("Yes").d("Sim")}
                                />
                                <FormControlLabel
                                    value="no"
                                    control={<Radio color="error" />}
                                    label={intl.get("No").d("Não")}
                                />
                            </RadioGroup>
                            {fieldErrors.contactAuthorization && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                    {fieldErrors.contactAuthorization}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Legal Document Dialog */}
            <LegalDocumentDialog
                open={documentDialogOpen}
                onClose={handleCloseDocument}
                document={legalDocument}
                loading={loadingDocument}
            />
        </Box>
    );
};
